import CancelIcon from '@mui/icons-material/Cancel'
import CheckIcon from '@mui/icons-material/Check'
import DeleteIcon from '@mui/icons-material/Delete'
import EditIcon from '@mui/icons-material/Edit'
import { Avatar, Box, CircularProgress, Grid, IconButton, ListItem, Tooltip, Typography, useTheme } from '@mui/material'
import { useQueryClient } from '@tanstack/react-query'
import { useUserItems } from 'api/aws/users/useUserItems'
import TextEditor from 'components/TextEditor/TextEditor'
import React, { RefObject, useState } from 'react'
import { BoxScroller } from 'styles/elements/styleElements'
import { CommentData, SelectedFile, User } from 'types'
import { useUpdateComment } from '../../api/aws/comments/useUpdateComment'
import { useUploadNoteAttachments } from '../../api/aws/s3/useUploadNoteAttachments'
import '../TextEditor/style.css'
import './Comments.css'
import PreviewAttachments from './PreviewAttachments'
import ReadOnlyRenderer from './ReadOnlyRenderer'
import ReadByIcon from './ReadyByIcon'

interface CommentCardProps {
  note: CommentData
  currentUser: User
  noteOwner: User | undefined
  editingContent: string
  setEditingContent: (html: string) => void
  onEditStart: (noteId: string) => void
  onOpenViewer: (attachments: any[], index: number) => void
  onDeleteNote: (note: CommentData) => void
  onDeleteClick: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>, comment: CommentData) => void
  onDeleteAttachment: (commentId: string, attachmentIndex: number) => void
  isEditing: boolean
  deletingNoteId: string | null
  editingNoteId: string | null
  setEditingNoteId: (id: string | null) => void
  editingFiles: SelectedFile[]
  setEditingFiles: (files: SelectedFile[]) => void
  setIsEditing: (editing: boolean) => void
  anchorEl: HTMLElement | null
  scrollRef: RefObject<HTMLUListElement | null>
  commentRefs: React.MutableRefObject<Record<string, HTMLLIElement | null>>
  confirmRefs: React.MutableRefObject<Record<string, HTMLDivElement | null>>
  onClosePopover: () => void
  isDeleting: boolean
  onMarkAsRead: (commentUuid: string) => void
}
const CommentCard: React.FC<CommentCardProps> = ({
  note,
  currentUser,
  noteOwner,
  editingContent,
  setEditingContent,
  onEditStart,
  onOpenViewer,
  onDeleteAttachment,
  onDeleteClick,
  onDeleteNote,
  isEditing,
  deletingNoteId,
  editingNoteId,
  editingFiles,
  setEditingNoteId,
  setEditingFiles,
  setIsEditing,
  anchorEl,
  scrollRef,
  commentRefs,
  confirmRefs,
  onClosePopover,
  isDeleting,
  onMarkAsRead,
}) => {
  const [isHovered, setIsHovered] = useState(false)
  const { data: users } = useUserItems()
  const theme = useTheme()
  const { mutateAsync: updateComment } = useUpdateComment()
  const { mutateAsync: uploadAttachments } = useUploadNoteAttachments()
  const queryClient = useQueryClient()
  const alreadyRead =
    (Array.isArray(note.readBy) && note.readBy.some(entry => entry.user_uuid === currentUser?.user_uuid)) ||
    (Array.isArray(note?.owner) && note?.owner?.[0] === currentUser?.user_uuid)
  const [hasBeenRead, setHasBeenRead] = useState<boolean>(alreadyRead)
  const handleMouseEnter = () => {
    if (!hasBeenRead) {
      setHasBeenRead(true)
      onMarkAsRead(note.comment_uuid)
    }
  }

  const handleCancel = () => {
    setIsEditing(false)
    setIsHovered(false)
  }
  const open = Boolean(anchorEl)
  const isBeingDeleted = deletingNoteId === note.comment_uuid
  const isNoteCreator = currentUser?.user_uuid === note?.owner?.[0]
  const isTaggedUser = note?.taggedUsers?.filter(user => user?.id === currentUser?.user_uuid)?.length > 0
  return (
    <>
      <ListItem
        ref={el => {
          commentRefs.current[note.comment_uuid] = el
        }}
        data-comment-uuid={note.comment_uuid}
        sx={{
          mb: 2,

          display: 'flex',
          flexDirection: 'column',
          alignItems: 'stretch',
          // If it’s been read, give it a light-green background:
          bgcolor: theme.palette.background.mentions, // original un‐read background
          borderRadius: '10px',
          p: 0,
          border: `1px solid ${
            deletingNoteId === note.comment_uuid ? theme.palette.text.errordim : theme.palette.text.primary
          }`,
          width: '100%',
          transition: 'all 0.3s ease',
          maxHeight: '40vh',
          overflow: 'hidden',
          // overflowY: 'auto',
        }}
        onMouseEnter={() => {
          handleMouseEnter()
          setIsHovered(true)
        }}
        onMouseLeave={() => setIsHovered(false)}
      >
        <BoxScroller sx={{ overflowY: 'auto' }}>
          {/* Row 1: Header with Avatar, Name/Date, Context Label, and Delete Button */}
          <Grid container alignItems="center" p={1}>
            <Grid item>
              <Box sx={{ position: 'relative', display: 'inline-flex' }}>
                {!hasBeenRead && (
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 0,
                      right: 0,
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      bgcolor: 'text.warning',
                      animation: 'pulse 1.5s infinite',
                      '@keyframes pulse': {
                        '0%': { transform: 'scale(0.8)', opacity: 0.7 },
                        '50%': { transform: 'scale(1.4)', opacity: 0.2 },
                        '100%': { transform: 'scale(0.8)', opacity: 0.7 },
                      },
                    }}
                  />
                )}
                {noteOwner && (
                  <Avatar
                    alt={noteOwner.name}
                    src={noteOwner.image_512 || noteOwner.imageUrl}
                    sx={{ width: 48, height: 48 }}
                  />
                )}
              </Box>
            </Grid>
            <Grid item xs>
              <Box sx={{ ml: 2 }}>
                <Typography sx={{ color: theme.palette.text.primary }} variant="subtitle2" fontWeight="bold">
                  {noteOwner?.name}
                </Typography>
                <Typography variant="caption">{new Date(note.createdAt).toLocaleString()}</Typography>
              </Box>
            </Grid>
            <Grid item>
              {note?.context === 'seo' ? (
                <Typography sx={{ fontSize: 12, color: 'rgb(238, 111, 141)' }}>SEO</Typography>
              ) : note?.context === 'nco' ? (
                <Typography sx={{ fontSize: 12, color: 'rgb(52, 211, 145)' }}>NCO</Typography>
              ) : note?.context === 'creative' ? (
                <Typography sx={{ fontSize: 12, color: 'rgb(150, 88, 238)' }}>Creative</Typography>
              ) : note?.context === 'website' ? (
                <Typography sx={{ fontSize: 12, color: 'rgb(112, 84, 240)' }}>Website</Typography>
              ) : note?.context === 'mtb' ? (
                <Typography sx={{ fontSize: 12, color: 'rgb(20, 119, 248)' }}>MTB</Typography>
              ) : note?.context === 'projects' ? (
                <Typography sx={{ fontSize: 12, color: 'rgb(245, 108, 53)' }}>Projects</Typography>
              ) : note?.context === 'deals' ? (
                <Typography sx={{ fontSize: 12, color: theme.palette.icon.deals }}>
                  {note?.dealName ?? note?.context}
                </Typography>
              ) : note?.context === 'integration' ? (
                <Typography sx={{ fontSize: 12, color: 'rgb(116, 238, 201)' }}>Integration</Typography>
              ) : note?.PK?.startsWith('OPTIMIZATION_') ? (
                <Typography sx={{ fontSize: 12, color: 'rgb(65, 190, 169)' }}>{note?.identifier}</Typography>
              ) : note?.PK?.startsWith('MONTHLY_TASK_') ? (
                <Typography sx={{ fontSize: 12, color: 'rgb(20, 119, 248)' }}>{note?.identifier}</Typography>
              ) : (
                <Typography sx={{ fontSize: 12, color: theme.palette.text.highlight }}>{note?.context}</Typography>
              )}
            </Grid>
            {(isBeingDeleted || isHovered) && !isEditing && isNoteCreator && (
              <Box
                sx={{
                  position: 'absolute',
                  top: 3,
                  right: 3,
                  display: 'flex',
                  gap: 5,
                  backgroundColor: isBeingDeleted ? 'text.errordim' : 'transparent',
                  backdropFilter: 'blur(10px)',
                  borderRadius: 1,
                  boxShadow: 1,
                  p: 0.5,
                  px: 2.5,
                  zIndex: 10,
                  // minWidth: '25%',
                  transition: 'all 0.3s ease',
                  opacity: isHovered ? 1 : 0,
                  justifyContent: 'space-between',
                }}
              >
                {isBeingDeleted ? (
                  <Grid item xs={12}>
                    <Box
                      ref={(el: HTMLDivElement | null) => {
                        confirmRefs.current[note.comment_uuid] = el
                      }}
                      sx={{
                        py: 1,
                        px: 3,
                        borderRadius: 1,
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        gap: 2,
                        zIndex: 1, // above card contents but inside the card
                      }}
                    >
                      <Typography sx={{ fontWeight: 500, mr: 1 }}>
                        {isDeleting ? 'Deleting...' : 'Confirm deletion?'}
                      </Typography>
                      <IconButton onClick={() => onDeleteNote(note)} sx={{ color: 'success.main' }}>
                        {isDeleting ? <CircularProgress size={25} sx={{ color: 'text.primary' }} /> : <CheckIcon />}
                      </IconButton>
                      <IconButton
                        onClick={() => {
                          onClosePopover()
                          setIsHovered(false)
                        }}
                        sx={{ color: 'text.secondary' }}
                        disabled={isDeleting}
                      >
                        <CancelIcon sx={{ color: isDeleting ? 'text.secondary' : 'text.primary' }} />
                      </IconButton>
                    </Box>
                  </Grid>
                ) : (
                  <>
                    <Tooltip title="Edit Comment" placement="bottom-start">
                      <IconButton
                        onClick={() => {
                          setEditingContent(note.contents)
                          onEditStart(note.comment_uuid)
                        }}
                      >
                        <EditIcon sx={{ height: 26, width: 26, color: 'text.highlight' }} />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete Comment" placement="bottom-start">
                      <IconButton onClick={e => onDeleteClick(e, note)}>
                        <DeleteIcon sx={{ height: 26, width: 26, ':hover': { color: 'text.error' } }} />
                      </IconButton>
                    </Tooltip>
                  </>
                )}
              </Box>
            )}
          </Grid>

          <Grid container sx={{ p: 1, borderRadius: '10px' }}>
            {/* Row 2: Note Contents */}
            <Grid item xs={12} sx={{ m: 0 }}>
              {isEditing && editingNoteId === note.comment_uuid ? (
                <Box>
                  <TextEditor
                    newNoteContent={editingContent}
                    onContentChange={setEditingContent}
                    onSave={async (html: string) => {
                      let uploaded = []
                      if (editingFiles.length > 0) {
                        const rawFiles = editingFiles.map(f => f.file)
                        uploaded = await uploadAttachments({ files: rawFiles, orgId: note?.orgId }) // You already have this hook
                      }

                      await updateComment(
                        {
                          PK: note.PK,
                          SK: note.SK,
                          updates: [
                            { fieldName: 'contents', newValue: html },
                            ...(uploaded.length > 0
                              ? [
                                  {
                                    fieldName: 'attachments',
                                    newValue: [...(note.attachments || []), ...uploaded],
                                  },
                                ]
                              : []),
                          ],
                        },
                        {
                          onSuccess: (_, variables) => {
                            console.log('onSuccess variables updateComment:', variables)
                          },
                        }
                      )

                      setEditingNoteId(null)
                      setEditingContent('')
                      setEditingFiles([])
                      setIsEditing(false)
                    }}
                    selectedTask={null}
                    selectedFiles={[]}
                    setSelectedFiles={() => {}}
                    onMentionsChange={() => {}}
                    onRemoveFile={() => {}}
                    onUpload={() => {}}
                    height="150px"
                    isEditing={isEditing}
                    onCancel={handleCancel}
                  />
                </Box>
              ) : (
                <ReadOnlyRenderer content={note.contents} />
              )}
            </Grid>
          </Grid>
        </BoxScroller>
        <Grid
          container
          sx={{
            alignItems: 'center',
            width: '100%',
            borderTop: '1px solid',
            borderColor: 'divider',
            py: 0.5,
            px: 0.5,
          }}
        >
          <Grid item xs={6}>
            {note.attachments && note.attachments.length > 0 && (
              <Box
                sx={{
                  // px: 2,
                  // py: 1.5,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  position: 'sticky',
                  bottom: 0,
                  zIndex: 1,
                }}
              >
                <PreviewAttachments
                  note={note}
                  // openFrame={openFrame}
                  onDeleteAttachment={index => onDeleteAttachment(note.comment_uuid, index)}
                  onOpenViewer={index => onOpenViewer(note.attachments, index)}
                  isNoteCreator={isNoteCreator}
                  // handleDownload={handleDownload}
                />
              </Box>
            )}
          </Grid>
          <Grid item xs={6}>
            {note.readBy && note.readBy.length > 0 ? (
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', width: '100%' }}>
                <ReadByIcon note={note} users={users} />
              </Box>
            ) : (
              <Box sx={{ height: 24 }} />
            )}
          </Grid>
        </Grid>
      </ListItem>
    </>
  )
}

export default CommentCard
